export const apacCountryCodes = ['SG', 'HK'];

export enum UserAuthority {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  UPDATE_PROSPECT = 'UPDATE_PROSPECT',
  UPDATE_PARTNER = 'UPDATE_PARTNER',
  READ = 'READ',
  ADMIN_TASK_EXECUTE = 'ADMIN_TASK_EXECUTE',
  DELETE = 'DELETE',
  PROTECTED_CU_EMPLOYEE_FLAG = 'PROTECTED_CU_EMPLOYEE_FLAG',
  PROTECTED_U_PEP = 'PROTECTED_U_PEP',
  READ_ALL = 'READ_ALL',
  VIEW = 'VIEW',
  PROTECTED_U_HOLD_MAIL_DISPATCH_FLAG = 'PROTECTED_U_HOLD_MAIL_DISPATCH_FLAG',
  PROTECTED_U_IDENTIFICATION = 'PROTECTED_U_IDENTIFICATION',
  //tab related start
  TAB_PARTNERS = 'TAB_PARTNERS',
  TAB_MY_PROSPECTS = 'TAB_MY_PROSPECTS',
  TAB_TEAM_PROSPECTS = 'TAB_TEAM_PROSPECTS',
  TAB_INACTIVE_PARTNERS = 'TAB_INACTIVE_PARTNERS',
  TAB_ALL_PARTNERS = 'TAB_ALL_PARTNERS',
  //tab related end
  WITHDRAW_PROSPECT = 'WITHDRAW_PROSPECT',
  REJECT_PROSPECT = 'REJECT_PROSPECT',
  FTH_ACCESS = 'FTH_ACCESS',
  ASSIGNABLE_RM = 'ASSIGNABLE_RM',
  STATUS_APPROVE = 'STATUS_APPROVE',
  TAB_IN_APPROVAL = 'TAB_IN_APPROVAL',
  READ_ALL_SPECIALIST_SCOPE = 'READ_ALL_SPECIALIST_SCOPE',
  PROSPECT_REACTIVATE = 'PROSPECT_REACTIVATE',
  REASSIGN_RM = 'REASSIGN_RM',
  RMA_ACCESS = 'RMA_ACCESS',
  //distribution center related role
  SEARCH_PARTNER = 'SEARCH_PARTNER',
  DISTRIBUTE_AGREEMENT = 'DISTRIBUTE_AGREEMENT',
  SAVE_AGREEMENT = 'SAVE_AGREEMENT',
  SEARCH_AGREEMENT = 'SEARCH_AGREEMENT',
  DOWNLOAD_AGREEMENT = 'DOWNLOAD_AGREEMENT',
  //from partner-data-mgmt, needed for adhoc screening tab display
  ADHOC_SCREENING_ACCESS = 'ADHOC_SCREENING_ACCESS',
  //from portal
  QUICK_SEARCH = 'QUICK_SEARCH',
}

export interface UserDTO {
  email: string;
  givenName: string;
  sureName: string;
  userLocation: string;
  username: string;
  roleList: string[];
  authorities: UserAuthority[];
  rm: boolean;
  rma: boolean;
  fth: boolean;
}

export interface UserAuthorityDTO {
  partner: UserAuthority[];
  note: UserAuthority[];
  taskManager: UserAuthority[];
  distributionCenter: UserAuthority[];
}

export class User {
  email: string;
  givenName: string;
  sureName: string;
  userLocation: string;
  username: string;
  roleList: string[];
  authorities: string[];
  hasPartnerCreationAccess: boolean;
  hasPartnerEditAccess: boolean;
  hasProspectEditAccess: boolean;
  hasNoteCreationAccess: boolean;
  hasNoteReadAccess: boolean;
  hasNoteEditAccess: boolean;
  hasGdprRequestAccess: boolean;
  hasGdprDeletionAccess: boolean;
  hasPartnersTabAccess: boolean;
  hasMyProspectsTabAccess: boolean;
  hasTeamProspectsTabAccess: boolean;
  hasInactivePartnersTabAccess: boolean;
  hasAllPartnersTabAccess: boolean;
  hasEditHoldMailDispatchAccess: boolean;
  hasIdentificationUpdateAuthority: boolean;
  hasWithdrawProspectAuthority: boolean;
  hasRejectProspectAuthority: boolean;
  hasFrontTeamHeadAccessAuthority: boolean;
  hasAssignableRmAuthority: boolean;
  hasStatusApproveAuthority: boolean;
  hasInApprovalTabAuthority: boolean;
  hasQuickSearchAuthority: boolean;
  defaultPartnerTabIndex: any;
  dataProtectionRegulationLevel: number | null;
  hasBjbEmployeeChangeAuthority: boolean;
  hasProspectReactivateAuthority: boolean;
  userAuthorities: UserAuthorityDTO;
  isApac: boolean;
  isBrManager: boolean;
  hasReadAllSpecialistAuthority: boolean;
  hasReassignRmAuthority: boolean;
  hasCDTSearchPartner: boolean;
  hasCDTDistributeAgreement: boolean;
  hasCDTSaveAgreement: boolean;
  hasCDTSearchAgreement: boolean;
  hasCDTDownloadAgreement: boolean;
  userBusinessEntityCountryCode: number;
  hasRMAAccessAuthority: boolean;

  constructor(
    email: string,
    givenName: string,
    sureName: string,
    userLocation: string,
    username: string,
    roleList: string[],
    authorities: UserAuthority[],
    userAuthorities: UserAuthorityDTO,
    userBusinessEntityCountryCode: number,
  ) {
    this.email = email;
    this.givenName = givenName;
    this.sureName = sureName;
    this.userLocation = userLocation;
    this.username = username;
    this.roleList = roleList;
    this.authorities = authorities;

    this.hasQuickSearchAuthority = authorityListIncludeAtLeastOne([UserAuthority.QUICK_SEARCH], authorities);

    this.hasWithdrawProspectAuthority = authorityListIncludeAtLeastOne(
      [UserAuthority.WITHDRAW_PROSPECT],
      userAuthorities?.partner,
    );
    this.hasRejectProspectAuthority = authorityListIncludeAtLeastOne(
      [UserAuthority.REJECT_PROSPECT],
      userAuthorities?.partner,
    );
    this.hasFrontTeamHeadAccessAuthority = authorityListIncludeAtLeastOne(
      [UserAuthority.FTH_ACCESS],
      userAuthorities?.partner,
    );
    this.hasAssignableRmAuthority = authorityListIncludeAtLeastOne(
      [UserAuthority.ASSIGNABLE_RM],
      userAuthorities?.partner,
    );
    this.hasPartnerCreationAccess = authorityListIncludeAtLeastOne([UserAuthority.CREATE], userAuthorities?.partner);
    this.hasPartnerEditAccess = authorityListIncludeAtLeastOne(
      [UserAuthority.UPDATE, UserAuthority.UPDATE_PARTNER],
      userAuthorities?.partner,
    );
    this.hasProspectEditAccess = authorityListIncludeAtLeastOne(
      [UserAuthority.UPDATE, UserAuthority.UPDATE_PROSPECT],
      userAuthorities?.partner,
    );
    this.hasNoteCreationAccess = authorityListIncludeAtLeastOne([UserAuthority.CREATE], userAuthorities.note);
    this.hasNoteReadAccess = authorityListIncludeAtLeastOne(
      [UserAuthority.READ, UserAuthority.READ_ALL],
      userAuthorities.note,
    );
    this.hasNoteEditAccess = authorityListIncludeAtLeastOne([UserAuthority.UPDATE], userAuthorities.note);
    this.hasGdprRequestAccess = authorityListIncludeAtLeastOne(
      [UserAuthority.ADMIN_TASK_EXECUTE],
      userAuthorities?.partner,
    );
    this.hasGdprDeletionAccess = authorityListIncludeAtLeastOne(
      [UserAuthority.DELETE, UserAuthority.ADMIN_TASK_EXECUTE],
      userAuthorities?.partner,
    );
    this.defaultPartnerTabIndex = authorityListIncludeAtLeastOne(
      [UserAuthority.READ_ALL, UserAuthority.ADMIN_TASK_EXECUTE],
      userAuthorities?.partner,
    )
      ? 'ALL_PARTNERS'
      : 'PARTNERS';
    this.hasPartnersTabAccess = authorityListIncludeAtLeastOne([UserAuthority.TAB_PARTNERS], userAuthorities?.partner);
    this.hasMyProspectsTabAccess = authorityListIncludeAtLeastOne(
      [UserAuthority.TAB_MY_PROSPECTS],
      userAuthorities?.partner,
    );
    this.hasTeamProspectsTabAccess = authorityListIncludeAtLeastOne(
      [UserAuthority.TAB_TEAM_PROSPECTS],
      userAuthorities?.partner,
    );
    this.hasInactivePartnersTabAccess = authorityListIncludeAtLeastOne(
      [UserAuthority.TAB_INACTIVE_PARTNERS],
      userAuthorities?.partner,
    );
    this.hasAllPartnersTabAccess = authorityListIncludeAtLeastOne(
      [UserAuthority.TAB_ALL_PARTNERS, UserAuthority.ADMIN_TASK_EXECUTE],
      userAuthorities?.partner,
    );
    this.dataProtectionRegulationLevel = null;
    this.hasEditHoldMailDispatchAccess = authorityListIncludeAtLeastOne(
      [UserAuthority.PROTECTED_U_HOLD_MAIL_DISPATCH_FLAG],
      userAuthorities?.partner,
    );
    this.hasIdentificationUpdateAuthority = authorityListIncludeAtLeastOne(
      [UserAuthority.PROTECTED_U_IDENTIFICATION],
      userAuthorities?.partner,
    );
    this.hasBjbEmployeeChangeAuthority = authorityListIncludeAtLeastOne(
      [UserAuthority.PROTECTED_CU_EMPLOYEE_FLAG],
      userAuthorities?.partner,
    );
    this.hasStatusApproveAuthority = authorityListIncludeAtLeastOne(
      [UserAuthority.STATUS_APPROVE],
      userAuthorities?.partner,
    );
    this.hasInApprovalTabAuthority = authorityListIncludeAtLeastOne(
      [UserAuthority.TAB_IN_APPROVAL],
      userAuthorities?.partner,
    );
    this.hasProspectReactivateAuthority = authorityListIncludeAtLeastOne(
      [UserAuthority.PROSPECT_REACTIVATE],
      userAuthorities?.partner,
    );
    this.userAuthorities = userAuthorities;
    this.isApac = false;
    this.isBrManager = false;
    this.hasReadAllSpecialistAuthority = authorityListIncludeAtLeastOne(
      [UserAuthority.READ_ALL_SPECIALIST_SCOPE],
      userAuthorities?.partner,
    );
    this.hasReassignRmAuthority = authorityListIncludeAtLeastOne([UserAuthority.REASSIGN_RM], userAuthorities?.partner);
    this.hasRMAAccessAuthority = authorityListIncludeAtLeastOne([UserAuthority.RMA_ACCESS], userAuthorities?.partner);
    this.hasCDTSearchPartner = authorityListIncludeAtLeastOne(
      [UserAuthority.SEARCH_PARTNER],
      userAuthorities.distributionCenter,
    );
    this.hasCDTDistributeAgreement = authorityListIncludeAtLeastOne(
      [UserAuthority.DISTRIBUTE_AGREEMENT],
      userAuthorities.distributionCenter,
    );
    this.hasCDTSaveAgreement = authorityListIncludeAtLeastOne(
      [UserAuthority.SAVE_AGREEMENT],
      userAuthorities.distributionCenter,
    );
    this.hasCDTSearchAgreement = authorityListIncludeAtLeastOne(
      [UserAuthority.SEARCH_AGREEMENT],
      userAuthorities.distributionCenter,
    );
    this.hasCDTDownloadAgreement = authorityListIncludeAtLeastOne(
      [UserAuthority.DOWNLOAD_AGREEMENT],
      userAuthorities.distributionCenter,
    );
    this.userBusinessEntityCountryCode = userBusinessEntityCountryCode;
  }
}

const authorityListIncludeAtLeastOne = (expectedAuthorities: UserAuthority[], actualAuthorities: UserAuthority[]) => {
  return actualAuthorities?.some((actualRole) => expectedAuthorities?.includes(actualRole));
};

export enum UserBusinessEntityCountryCode {
  SG = 70,
  HK = 186,
  CH = 27,
}
